import { useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { SortableTable, type Column } from '@/components/SortableTable'
import { usePersistedState } from '@/lib/usePersistedState'
import { formatValue } from '@/charts/palette'
import { projectStatus } from '@/features/projects/status'
import { fmtDate, fmtDateTime, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { crewsMessages } from '@/i18n/messages/crews'
import { Badge } from './Badge'
import { EditCrewDialog } from './EditCrewDialog'
import { CrewTabs, type CrewTab } from './CrewTabs'
import { PerformanceTab } from './PerformanceTab'
import { MapPhotosTab } from './MapPhotosTab'
import { AttendanceTab } from './AttendanceTab'
import { BonusTab } from './BonusTab'
import { NO_PROJECT, type CrewDetail, type CrewMember, type CrewProject, type CrewRosterEntry } from './types'

export function CrewDetailPage() {
  const t = useT(crewsMessages)
  const tc = useT(commonMessages)
  const { id } = useParams<{ id: string }>()
  const [days] = usePersistedState<number>('crews:days', 30)
  const [editing, setEditing] = useState(false)
  // The open tab lives in the URL, so a tab can be linked to and survives a reload.
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') ?? 'overview') as CrewTab
  const setTab = (next: CrewTab) => {
    const p = new URLSearchParams(params)
    if (next === 'overview') p.delete('tab'); else p.set('tab', next)
    setParams(p, { replace: true })
  }
  const { data, isPending, error } = useQuery<CrewDetail>({
    queryKey: ['crew', id, days],
    queryFn: () => api.get<CrewDetail>(`/api/crews/${id}?days=${days}`),
    enabled: Boolean(id),
  })

  const loader = usePageLoader(isPending, t('loadingCrew'))
  if (loader) return loader
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('notFound')}</p>

  const c = data.crew
  const currentLeader = data.leaderHistory.find((s) => !s.to)

  const memberColumns: Column<CrewMember>[] = [
    {
      key: 'name', header: t('colName'),
      sortValue: (m) => (m.name ?? '').toLowerCase(),
      render: (m) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{m.name ?? '—'}</span>
          {m.isLeader && <Badge tone="brand">{t('badgeLeader')}</Badge>}
          {m.isNew && <Badge tone="green">{t('badgeNew')}</Badge>}
          {!m.onRoster && <Badge tone="amber">{t('badgeNotOnRoster')}</Badge>}
          {!m.employed && <Badge tone="red">{t('badgeLeft')}</Badge>}
        </div>
      ),
    },
    { key: 'days', header: t('colDays'), align: 'right', sortValue: (m) => m.daysWorked, render: (m) => <span className="tabular">{m.daysWorked}</span> },
    { key: 'last', header: t('colLastDay'), sortValue: (m) => m.lastDay ?? '', render: (m) => fmtDate(m.lastDay) },
    { key: 'hired', header: t('colHired'), sortValue: (m) => m.startDate ?? '', render: (m) => fmtDate(m.startDate) },
  ]

  const rosterColumns: Column<CrewRosterEntry>[] = [
    { key: 'name', header: t('colName'), sortValue: (r) => (r.name ?? '').toLowerCase(), render: (r) => <span className="font-medium">{r.name ?? '—'}</span> },
    { key: 'added', header: t('colAdded'), sortValue: (r) => r.addedOn ?? '', render: (r) => fmtDate(r.addedOn) },
    {
      key: 'status', header: t('colStatus'),
      sortValue: (r) => (r.employed ? 0 : 2) + (r.checkedInRecently ? 0 : 1),
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.employed
            ? <Badge tone="grey">{t('employed')}</Badge>
            : <Badge tone="red">{r.endDate ? t('leftOn', { date: fmtDate(r.endDate) }) : t('left')}</Badge>}
          {r.checkedInRecently
            ? <Badge tone="green">{t('checkedIn')}</Badge>
            : <Badge tone="amber">{t('notCheckedIn')}</Badge>}
        </div>
      ),
    },
  ]

  const projectColumns: Column<CrewProject>[] = [
    {
      key: 'project', header: t('colProject'),
      sortValue: (p) => (p.name ?? '').toLowerCase(),
      render: (p) => p.projectId === NO_PROJECT ? (
        <span className="text-neutral-500">{tc('noProject')}</span>
      ) : (
        <div>
          {p.rtkSegments > 0 ? (
            // Straight to the RTK map with this project selected and framed.
            <Link to={`/rtk?project=${p.projectId}`} title={t('openOnMap')}
              className="font-medium text-brand-700 hover:underline dark:text-brand-100">
              {p.name ?? p.codeInternal ?? '—'} <span aria-hidden className="text-xs">↗</span>
            </Link>
          ) : (
            // Nothing to draw: a map link would open onto an empty map, so say why instead.
            <span className="font-medium" title={t('reportedOnlyHint')}>{p.name ?? p.codeInternal ?? '—'}</span>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-neutral-400">
            {p.rtkSegments > 0 ? (
              <>
                <Badge tone="green">{t('rtkCount', { count: p.rtkSegments })}</Badge>
                {p.crewRtkSegments === 0 && <span className="italic">{t('rtkByOthers')}</span>}
              </>
            ) : (
              <Badge tone="grey" title={t('reportedOnlyHint')}>⌀ {t('reportedOnly')}</Badge>
            )}
            {p.codeInternal && <span className="tabular">{p.codeInternal}</span>}
            <span aria-hidden>·</span>
            <Link to={`/projects/${p.projectId}`} className="hover:underline">{t('projectDetails')}</Link>
          </div>
        </div>
      ),
    },
    {
      key: 'status', header: t('colStatus'),
      sortValue: (p) => p.status ?? -1,
      render: (p) => {
        if (p.projectId === NO_PROJECT) return null
        const st = projectStatus(p.status)
        return (
          <span className="whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ color: st.color, backgroundColor: `color-mix(in srgb, ${st.color} 14%, transparent)` }}>
            {st.label}
          </span>
        )
      },
    },
    { key: 'leaders', header: t('colLeaders'), sortValue: (p) => (p.leaders ?? '').toLowerCase(), render: (p) => p.leaders ?? '—' },
    { key: 'days', header: t('colCrewDays'), align: 'right', sortValue: (p) => p.crewDays, render: (p) => <span className="tabular">{p.crewDays}</span> },
    { key: 'metres', header: t('colMetres'), align: 'right', sortValue: (p) => p.metres, render: (p) => <span className="tabular whitespace-nowrap">{formatValue(p.metres, 'm')}</span> },
    {
      key: 'period', header: t('colPeriod'), sortValue: (p) => p.lastWorked ?? '',
      render: (p) => (
        <span className="tabular whitespace-nowrap text-xs text-neutral-500">
          {p.firstWorked === p.lastWorked ? fmtDate(p.lastWorked) : `${fmtDate(p.firstWorked)} – ${fmtDate(p.lastWorked)}`}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <header>
        <Link to="/crews" className="text-xs text-neutral-500 hover:underline">← {t('back')}</Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{c.name ?? '—'}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
              {c.code && <span>{c.code}</span>}
              {c.company && <span>{c.company}</span>}
              <span>{c.lastCheckIn ? t('lastCheckIn', { date: fmtDate(c.lastCheckIn) }) : t('neverCheckedIn')}</span>
              <Badge tone={c.active ? 'green' : 'grey'}>{c.active ? t('active') : t('inactive')}</Badge>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20"
          >
            {tc('edit')}
          </button>
        </div>
      </header>

      <CrewTabs value={tab} onChange={setTab} />

      {tab === 'performance' && id && <PerformanceTab crewId={id} />}
      {tab === 'map' && id && <MapPhotosTab crewId={id} />}
      {tab === 'attendance' && id && <AttendanceTab crewId={id} />}
      {tab === 'bonus' && id && <BonusTab crewId={id} />}

      {tab === 'overview' && (
        <>
        {(c.leaderNotCheckedIn || c.bauleiterOutdatedInAu || c.notOnRoster > 0 || c.rosterLeft > 0) && (
          <ul className="space-y-1 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
            {c.leaderNotCheckedIn && <li>⚠ {t('warnLeaderNotCheckedIn')}</li>}
            {c.notOnRoster > 0 && <li>⚠ {t('warnNotOnRoster', { count: c.notOnRoster })}</li>}
            {c.rosterLeft > 0 && <li>⚠ {t('warnRosterLeft', { count: c.rosterLeft })}</li>}
            {c.bauleiterOutdatedInAu && <li>⚠ {t('warnBauleiterOutdated')}</li>}
          </ul>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <Card title={t('colLeader')} value={c.leader}>
            <h3 className="text-xs font-medium text-neutral-500">{t('sectionLeaderHistory')}</h3>
            {data.leaderHistory.length === 0 ? (
              <p className="text-xs text-neutral-400">{t('noHistory')}</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {data.leaderHistory.map((s, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{s.name ?? '—'}{s === currentLeader && <span className="ml-1.5 text-xs text-brand-600">({t('current')})</span>}</span>
                    <span className="tabular text-xs text-neutral-500">{fmtDate(s.from)} – {s.to ? fmtDate(s.to) : ''}</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
          <Card title={t('colBauleiter')} value={c.bauleiter}>
            <h3 className="text-xs font-medium text-neutral-500">{t('sectionBauleiterChanges')}</h3>
            {data.bauleiterHistory.length === 0 ? (
              <p className="text-xs text-neutral-400">{t('noHistory')}</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {data.bauleiterHistory.map((b, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>
                      {b.newBauleiter ?? '—'}
                      {b.previousBauleiter && <span className="ml-1.5 text-xs text-neutral-400">{t('previously', { name: b.previousBauleiter })}</span>}
                    </span>
                    <span className="tabular shrink-0 text-xs text-neutral-500">{fmtDateTime(b.at)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t('sectionMembers', { days: data.windowDays })}</h2>
          <p className="text-xs text-neutral-500">{t('membersHint')}</p>
          <SortableTable
            columns={memberColumns}
            rows={data.members}
            rowKey={(m) => m.employeeId}
            initialSort={{ key: 'days', dir: 'desc' }}
            empty={t('noMembers', { days: data.windowDays })}
          />
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t('sectionRoster')}</h2>
          <p className="text-xs text-neutral-500">{t('rosterHint')}</p>
          <SortableTable
            columns={rosterColumns}
            rows={data.roster}
            rowKey={(r) => r.employeeId}
            initialSort={{ key: 'status', dir: 'desc' }}
            empty={t('noRoster')}
          />
        </section>

        <details className="group space-y-2">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-lg font-semibold">
            <span aria-hidden className="text-sm text-neutral-400 transition-transform group-open:rotate-90">▶</span>
            {t('sectionProjects', { count: data.projects.filter((p) => p.projectId !== NO_PROJECT).length })}
          </summary>
          <p className="mt-2 text-xs text-neutral-500">{t('projectsHint')}</p>
          <div className="mt-2">
            <SortableTable
              columns={projectColumns}
              rows={data.projects}
              rowKey={(p) => p.projectId}
              initialSort={{ key: 'period', dir: 'desc' }}
              empty={t('noProjects')}
            />
          </div>
        </details>
        </>
      )}

      <p className="text-xs text-neutral-400">{t('readOnlyNote')}</p>
      {editing && <EditCrewDialog crew={c} onClose={() => setEditing(false)} />}
    </div>
  )
}

function Card({ title, value, children }: { title: string; value: string | null; children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div>
        <div className="text-xs text-neutral-500">{title}</div>
        <div className="text-lg font-semibold">{value ?? '—'}</div>
      </div>
      {children}
    </div>
  )
}
